/**
 * @fileoverview Frontend error handling utilities for Virtual PAT system
 *
 * This module provides centralized error handling for the frontend application:
 * 1. Axios interceptors for standardized API error handling
 * 2. Error transformation utilities to convert API errors to frontend-friendly format
 * 3. Centralized error logging and tracking
 */

import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

/**
 * Standard error response structure from the backend
 */
export interface ApiErrorResponse {
  error: {
    message: string;
    code: string;
    statusCode: number;
    details?: any;
    stack?: string;
  };
}

/**
 * Frontend error interface for consistent error handling
 */
export interface FrontendError {
  message: string;
  code: string;
  statusCode: number;
  isNetworkError?: boolean;
  isAuthError?: boolean;
  isValidationError?: boolean;
  isServerError?: boolean;
  details?: any;
  originalError?: any;
}

/**
 * Error severity levels for UI display
 */
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'error',
}

/**
 * Determines the appropriate severity level based on error type
 *
 * @param error - The frontend error object
 * @returns The appropriate severity level for UI display
 */
export const getErrorSeverity = (error: FrontendError): ErrorSeverity => {
  if (error.isNetworkError) return ErrorSeverity.WARNING;
  if (error.isAuthError) return ErrorSeverity.WARNING;
  if (error.isValidationError) return ErrorSeverity.WARNING;
  if (error.isServerError) return ErrorSeverity.ERROR;

  // Determine severity based on status code
  if (error.statusCode >= 500) return ErrorSeverity.ERROR;
  if (error.statusCode >= 400) return ErrorSeverity.WARNING;

  return ErrorSeverity.INFO;
};

/**
 * Transforms an API error into a standardized frontend error
 *
 * @param error - The error to transform
 * @returns A standardized frontend error object
 */
export const transformError = (error: any): FrontendError => {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    // Handle network errors (no response)
    if (axiosError.code === 'ECONNABORTED' || axiosError.message.includes('timeout')) {
      return {
        message: 'Request timed out. Please try again.',
        code: 'REQUEST_TIMEOUT',
        statusCode: 408,
        isNetworkError: true,
        originalError: error,
      };
    }

    if (axiosError.code === 'ERR_NETWORK' || !axiosError.response) {
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        statusCode: 0,
        isNetworkError: true,
        originalError: error,
      };
    }

    // Handle API error responses
    if (axiosError.response?.data?.error) {
      const apiError = axiosError.response.data.error;

      return {
        message: apiError.message,
        code: apiError.code,
        statusCode: apiError.statusCode,
        isAuthError: apiError.statusCode === 401 || apiError.statusCode === 403,
        isValidationError: apiError.statusCode === 400,
        isServerError: apiError.statusCode >= 500,
        details: apiError.details,
        originalError: error,
      };
    }

    // Handle other Axios errors
    return {
      message: axiosError.message || 'Unknown API error',
      code: axiosError.code || 'UNKNOWN_ERROR',
      statusCode: axiosError.response?.status || 500,
      isAuthError: axiosError.response?.status === 401 || axiosError.response?.status === 403,
      isValidationError: axiosError.response?.status === 400,
      isServerError: axiosError.response?.status ? axiosError.response.status >= 500 : false,
      originalError: error,
    };
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
      code: 'CLIENT_ERROR',
      statusCode: 400,
      originalError: error,
    };
  }

  // Handle unknown errors
  return {
    message: error?.toString() || 'Unknown error',
    code: 'UNKNOWN_ERROR',
    statusCode: 500,
    originalError: error,
  };
};

/**
 * Logs an error to the appropriate tracking system
 *
 * @param error - The error to log
 * @param context - Additional context information
 */
export const logError = (error: FrontendError, context?: Record<string, any>): void => {
  // Filter out known development-only errors
  const isDevelopmentError = (err: FrontendError): boolean => {
    // Skip Vite HMR WebSocket errors
    if (
      err.message?.includes('WebSocket closed without opened') &&
      err.originalError?.stack?.includes('@vite/client')
    ) {
      return true;
    }

    // Skip other Vite-related errors
    if (
      err.originalError?.stack?.includes('@vite/') ||
      err.originalError?.stack?.includes('/@vite/')
    ) {
      return true;
    }

    return false;
  };

  // Skip logging development-only errors
  if (isDevelopmentError(error)) {
    console.debug('[Dev Error - Ignored]', error.message);
    return;
  }

  // Always log to console for debugging
  console.error('[Error]', error, context);

  // Log to backend in all environments for admin panel visibility
  try {
    // Get current user info for better logging
    const userId = localStorage.getItem('userId') || 'anonymous';
    const user = localStorage.getItem('user');
    let userName = 'unknown';
    try {
      if (user) {
        const userData = JSON.parse(user);
        userName = userData.name || userData.username || 'unknown';
      }
    } catch {
      // ignore parsing errors
    }

    // Use relative path for API calls to work in all environments
    const logEndpoint = '/api/log-client-error';

    console.debug(`Logging client error to: ${logEndpoint}`, { userId, userName });

    fetch(logEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Include auth token if available
        ...(localStorage.getItem('token') && {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }),
      },
      body: JSON.stringify({
        error: error.message,
        code: error.code,
        statusCode: error.statusCode,
        type: error.code,
        message: error.message,
        details: error.details,
        context: {
          ...context,
          component: context?.component || 'unknown',
          userId,
          userName,
          path: window.location.pathname,
        },
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        userId,
        stack: error.originalError?.stack || '',
        stackTrace: error.originalError?.stack || '',
        timestamp: new Date().toISOString(),
        receivedAt: new Date().toISOString(),
      }),
    })
      .then(response => {
        if (!response.ok) {
          console.error(`Error logging failed with status: ${response.status}`);
        } else {
          console.debug('Error successfully logged to backend');
        }
      })
      .catch(err => {
        console.error('Failed to send error log to server:', err);
      });

    // If Sentry is available, log there too
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error.originalError || error);
    }
  } catch (loggingError) {
    // Fallback to console if logging fails
    console.error('[Error Logging Failed]', loggingError);
    console.error('[Original Error]', error);
  }
};

/**
 * Configures an Axios instance with error handling interceptors
 *
 * @param axiosInstance - The Axios instance to configure
 * @returns The configured Axios instance
 */
export const setupAxiosInterceptors = (axiosInstance: AxiosInstance): AxiosInstance => {
  // Request interceptor
  axiosInstance.interceptors.request.use(
    config => {
      // Add request ID for tracking
      config.headers = config.headers || {};
      config.headers['X-Request-ID'] = generateRequestId();

      // Add authentication token if available
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    error => {
      // Handle request errors
      const frontendError = transformError(error);
      logError(frontendError, { phase: 'request' });
      return Promise.reject(frontendError);
    }
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      // You can add response transformations here if needed
      return response;
    },
    error => {
      // Transform and log the error
      const frontendError = transformError(error);
      logError(frontendError, { phase: 'response' });

      // Special handling for authentication errors
      if (frontendError.isAuthError) {
        // Redirect to login page if not already there
        if (!window.location.pathname.includes('/login')) {
          // Store the current location to redirect back after login
          sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
          window.location.href = '/login';
        }
      }

      // Special handling for rate limiting
      if (frontendError.statusCode === 429) {
        // Show user-friendly message for rate limiting
        frontendError.message = 'Too many requests. Please try again later.';
      }

      // Special handling for maintenance mode
      if (frontendError.statusCode === 503) {
        frontendError.message = 'Service temporarily unavailable. We are performing maintenance.';
      }

      return Promise.reject(frontendError);
    }
  );

  return axiosInstance;
};

/**
 * Creates a new Axios instance with error handling interceptors
 *
 * @param baseURL - The base URL for API requests
 * @param timeout - Request timeout in milliseconds (default: 30000)
 * @returns Configured Axios instance
 */
export const createApiClient = (baseURL: string, timeout: number = 30000): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return setupAxiosInterceptors(instance);
};

/**
 * Global error handler for unhandled promise rejections
 */
export const setupGlobalErrorHandlers = (): void => {
  window.addEventListener('unhandledrejection', event => {
    const error = transformError(event.reason);
    logError(error, { type: 'unhandledRejection' });
  });

  window.addEventListener('error', event => {
    const error = transformError(event.error || event.message);
    logError(error, { type: 'uncaughtError' });
  });
};

/**
 * Centralized error handler utility
 *
 * @param error - The error to handle
 * @param options - Options for error handling
 * @returns The transformed frontend error
 */
export const handleError = (
  error: any,
  options: {
    showNotification?: boolean;
    notificationDuration?: number;
    rethrow?: boolean;
    context?: Record<string, any>;
  } = {}
): FrontendError => {
  const {
    showNotification = false,
    notificationDuration = 5000,
    rethrow = false,
    context = {},
  } = options;

  // Transform the error to our standard format
  const frontendError = transformError(error);

  // Log the error
  logError(frontendError, context);

  // Show notification if requested
  if (showNotification && window.dispatchEvent) {
    // Use custom event to trigger notification
    // This will be caught by a global notification handler
    window.dispatchEvent(
      new CustomEvent('app:error', {
        detail: {
          error: frontendError,
          duration: notificationDuration,
        },
      })
    );
  }

  // Rethrow if requested
  if (rethrow) {
    throw frontendError;
  }

  return frontendError;
};

/**
 * Generates a unique request ID for tracking
 */
const generateRequestId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// Add TypeScript declaration for Sentry in window object
declare global {
  interface Window {
    Sentry?: {
      captureException: (error: any) => void;
    };
  }
}

export default {
  transformError,
  logError,
  setupAxiosInterceptors,
  createApiClient,
  setupGlobalErrorHandlers,
  getErrorSeverity,
  handleError,
};
